import React from 'react';
import { defineMessages } from 'react-intl-next';
import type { ReactNode } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

export type FormatHeaderMessageType = {
	localizedHeaderTimestamp: string;
	isSavedIndicator: boolean;
	hasVersionHistory: boolean;
	isPageOrBlog: boolean;
	isEditMode: boolean;
	versionNumber: number;
	createChangeLink: (timestamp: ReactNode) => React.JSX.Element;
	formatMessage: any;
};

export type FormatTooltipMessageType = {
	localizedTooltipTimestamp: string;
	isEditMode: boolean;
	isPageOrBlog: boolean;
	versionNumber: number;
	fullName: string;
	formatMessage: any;
};

const i18n = defineMessages({
	saved: {
		id: 'content-types-header.last-edited.saved',
		description:
			'Time string in last edited shortened header timestamp that indicates last updates were saved',
		defaultMessage: 'Saved',
	},
	shortJustNow: {
		id: 'content-types-header.last-edited.shortJustNow',
		description:
			'Time string in last edited shortened header timestamp that indicates last change was just now',
		defaultMessage: 'just now',
	},
	shortXMinutesAgo: {
		id: 'content-types-header.last-edited.shortXMinutesAgo',
		description:
			'Time string in last edited shortened header timestamp that indicates last change was x minutes ago',
		defaultMessage: '{timeAgo}m ago',
	},
	shortXHoursAgo: {
		id: 'content-types-header.last-edited.shortXHoursAgo',
		description:
			'Time string in last edited shortened header timestamp that indicates last change was x hours ago',
		defaultMessage: '{timeAgo}h ago',
	},
	shortXDaysAgo: {
		id: 'content-types-header.last-edited.shortXDaysAgo',
		description:
			'Time string in last edited shortened header timestamp that indicates last change was x days ago',
		defaultMessage: '{timeAgo}d ago',
	},
	tooltipFullDate: {
		id: 'content-types-header.last-edited.tooltipFullDate',
		description:
			'Expanded time string in last edited for tooltip that indicates last change was on (date) at (time)',
		defaultMessage: '{date} at {time}',
	},
	tooltipDate: {
		id: 'content-types-header.last-edited.tooltipDate',
		description:
			'Expanded time string in last edited for tooltip that indicates last change was >2 days ago on (date)',
		// eslint-disable-next-line @atlassian/i18n/no-useless-message
		defaultMessage: '{date}',
	},
	edited: {
		id: 'content-types-header.last-edited.edited',
		defaultMessage: 'Edited {timestamp}',
		description:
			'Timestamp string in the object header that indicates the content was edited at the time in timestamp',
	},
	published: {
		id: 'content-types-header.last-edited.published',
		defaultMessage: 'Published {timestamp}',
		description:
			'Timestamp string in the object header that indicates the content was published at the time in timestamp',
	},
	updated: {
		id: 'content-types-header.last-edited.updated',
		defaultMessage: 'Updated {timestamp}',
		description:
			'Timestamp string in object header that indicates the content was published at the time in timestamp',
	},
	editedOn: {
		id: 'content-types-header.last-edited.editedOn',
		defaultMessage: 'Edited by {contributor} on {timestamp}',
		description:
			'Timestamp string in the tooltip that indicates the content was edited at the time in timestamp by some contributor',
	},
	publishedOn: {
		id: 'content-types-header.last-edited.publishedOn',
		defaultMessage: 'Published by {contributor} on {timestamp}',
		description:
			'Timestamp string in the tooltip that indicates the content was published at the time in timestamp by some contributor',
	},
	updatedOn: {
		id: 'content-types-header.last-edited.updatedOn',
		defaultMessage: 'Updated by {contributor} on {timestamp}',
		description:
			'Timestamp string in the tooltip that indicates the content was published at the time in timestamp by some contributor',
	},
});

const MILLISECONDS_TO_SECONDS = 1000;
const MILLISECONDS_TO_MINUTES = 60 * 1000;
const MILLISECONDS_TO_HOUR = 60 * 60 * 1000;
const MILLISECONDS_TO_DAY = 24 * 60 * 60 * 1000;

/**
 * Formats full server date in shortened form ('MMM DD') if in the same calendar year, otherwise uses long form ('MMM DD, YYYY)
 */
const formatfriendlyWhen = (friendlyWhen: string) => {
	const now = new Date();
	const currentYear = now.getUTCFullYear();

	if (friendlyWhen.includes(currentYear.toString())) {
		// We expect that full server date (version.friendlyWhen) is of the format 'MMM DD, YYYY' (Jan 01, 2025) format for US_en
		// If it is not in this format depending on localization, we take the entire date
		const splitDate = friendlyWhen.split(',');
		return splitDate.length === 2 ? splitDate[0] : friendlyWhen;
	}

	return friendlyWhen;
};

/**
 * Takes lastEditedTime as Date object and converts to shortened relative friendly timestamp format for any relative timeframe.
 * isSavedIndicator is a boolean representing whether the timestamp is the shortened 'Saved' format
 *
 * For > 2 days uses server timestamp to satisfy hydration and SSR
 */
export const lastEditedHeaderFriendlyFormatter = (
	lastEditedTime: Date,
	friendlyWhen: string,
	isEditMode: boolean,
	formatMessage: any,
	renderServerPlaceholder: boolean,
) => {
	let headerTimestamp: string;
	const timeNow = new Date();
	let currentTimestamp;
	if (renderServerPlaceholder && window.__SSR_CURRENT_DATE__) {
		currentTimestamp = window.__SSR_CURRENT_DATE__;
	} else {
		currentTimestamp = timeNow.getTime();
	}
	const timeDifference = currentTimestamp - lastEditedTime.getTime();
	let isSavedIndicator = false;

	if (timeDifference / MILLISECONDS_TO_SECONDS < 60) {
		if (isEditMode) {
			headerTimestamp = formatMessage(i18n.saved);
			isSavedIndicator = true;
		} else {
			headerTimestamp = formatMessage(i18n.shortJustNow);
		}
	} else if (timeDifference / MILLISECONDS_TO_MINUTES < 60) {
		const minutes = Math.floor(timeDifference / MILLISECONDS_TO_MINUTES);
		headerTimestamp = formatMessage(i18n.shortXMinutesAgo, { timeAgo: minutes });
	} else if (timeDifference / MILLISECONDS_TO_HOUR < 24) {
		const hours = Math.floor(timeDifference / MILLISECONDS_TO_HOUR);
		headerTimestamp = formatMessage(i18n.shortXHoursAgo, { timeAgo: hours });
	} else if (timeDifference / MILLISECONDS_TO_HOUR < 48) {
		const days = Math.floor(timeDifference / MILLISECONDS_TO_DAY);
		headerTimestamp = formatMessage(i18n.shortXDaysAgo, { timeAgo: days });
	} else {
		// After 48 hours, include date in format MMM DD (shortened month 'Jan', for US_en) from server
		headerTimestamp = formatfriendlyWhen(friendlyWhen);
	}

	return { headerTimestamp, isSavedIndicator };
};

/**
 * Takes lastEditedTime as Date object and converts to expanded tooltip timestamp format.
 */
export const lastEditedTooltipTimestampFormatter = (
	lastEditedTime: Date,
	friendlyWhen: string,
	formatMessage: any,
	formatTime: any,
	formatDate: any,
	timeZone: string | undefined,
	renderServerPlaceholder: boolean,
) => {
	let tooltipTimestamp: string;
	const timeNow = new Date();
	let currentTimestamp;
	if (renderServerPlaceholder && window.__SSR_CURRENT_DATE__) {
		currentTimestamp = window.__SSR_CURRENT_DATE__;
	} else {
		currentTimestamp = timeNow.getTime();
	}
	const timeDifference = currentTimestamp - lastEditedTime.getTime();

	// Format client timestamp for use when < 48 hours in the past
	const monthAndDay = formatDate(lastEditedTime, {
		month: 'short',
		day: 'numeric',
		timeZone,
	});
	const formattedTime = formatTime(lastEditedTime, {
		hour: 'numeric',
		minute: 'numeric',
		timeZone,
	});

	if (timeDifference / MILLISECONDS_TO_HOUR < 48) {
		tooltipTimestamp = formatMessage(i18n.tooltipFullDate, {
			date: monthAndDay,
			time: formattedTime,
		});
	} else {
		// Use server friendlyWhen here because we want it to match the header timestamp
		tooltipTimestamp = formatMessage(i18n.tooltipDate, { date: friendlyWhen });
	}

	return tooltipTimestamp;
};

const focusBorderStyles = xcss({
	':focus-visible': {
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineColor: 'color.border.focused',
		outlineOffset: 'space.025',
	},
});

/**
 * Format header message by appending localized header timestamp to action verbage, distinguishing between
 * 'published' / 'updated' / 'edited'. Adds a link to version history if it exists.
 */
export const formatHeaderMessage = ({
	localizedHeaderTimestamp,
	isSavedIndicator,
	hasVersionHistory,
	isPageOrBlog,
	isEditMode,
	versionNumber,
	createChangeLink,
	formatMessage,
}: FormatHeaderMessageType) => {
	if (isSavedIndicator) {
		return hasVersionHistory ? (
			createChangeLink(localizedHeaderTimestamp)
		) : (
			<Box tabIndex={0} role="tooltip" xcss={focusBorderStyles}>
				{localizedHeaderTimestamp}
			</Box>
		);
	}

	let headerMessage: ReactNode | null;
	if (isPageOrBlog) {
		let formattedTimestamp: any;
		if (isEditMode) {
			formattedTimestamp = formatMessage(i18n.edited, { timestamp: localizedHeaderTimestamp });
		} else {
			formattedTimestamp =
				versionNumber > 1
					? formatMessage(i18n.updated, { timestamp: localizedHeaderTimestamp })
					: formatMessage(i18n.published, { timestamp: localizedHeaderTimestamp });
		}

		headerMessage = hasVersionHistory ? createChangeLink(formattedTimestamp) : formattedTimestamp;
	} else {
		const formattedTimestamp = formatMessage(i18n.edited, { timestamp: localizedHeaderTimestamp });
		headerMessage = hasVersionHistory ? createChangeLink(formattedTimestamp) : formattedTimestamp;
	}

	if (React.isValidElement(headerMessage)) {
		return headerMessage;
	} else {
		return (
			<Box tabIndex={0} role="tooltip" xcss={focusBorderStyles}>
				{headerMessage}
			</Box>
		);
	}
};

/**
 * Format tooltip message by appending localized tooltip timestamp action verbage,
 * distinguishing between 'published' / 'updated' / 'edited'
 */
export const formatTooltipMessage = ({
	localizedTooltipTimestamp,
	isEditMode,
	isPageOrBlog,
	versionNumber,
	fullName,
	formatMessage,
}: FormatTooltipMessageType) => {
	let tooltipMessage: any;
	if (isPageOrBlog) {
		if (isEditMode) {
			tooltipMessage = formatMessage(i18n.editedOn, {
				timestamp: localizedTooltipTimestamp,
				contributor: fullName,
			});
		} else {
			tooltipMessage =
				versionNumber > 1
					? formatMessage(i18n.updatedOn, {
							timestamp: localizedTooltipTimestamp,
							contributor: fullName,
						})
					: formatMessage(i18n.publishedOn, {
							timestamp: localizedTooltipTimestamp,
							contributor: fullName,
						});
		}
	} else {
		tooltipMessage = formatMessage(i18n.editedOn, {
			timestamp: localizedTooltipTimestamp,
			contributor: fullName,
		});
	}

	return tooltipMessage;
};
